import { isDate, isString, isNil } from 'lodash';
import { DateTime, DateTimeUnit, SystemZone, Zone } from 'luxon';

import { Maybe } from './globals';

export type DateLike = DateTime | Date | NovaDate | string;

export class NovaDateInvalidValueError extends Error {
  constructor(public readonly value: DateLike) {
    const message = `NovaDate: invalid value for date: ${value}`;
    super(message);
    this.message = message;
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  }
}

export class NovaDate {
  private readonly strFormats = [DateTime.fromISO, DateTime.fromRFC2822, DateTime.fromSQL] as const;

  constructor(private readonly value: DateLike) {}

  public static tzNow(): NovaDate {
    return new NovaDate(DateTime.now().toUTC());
  }

  private stringToDateTime(str: string): DateTime {
    const dt = this.strFormats.map(t => t(str)).find(t => t.isValid);
    if (!dt) {
      throw new NovaDateInvalidValueError(str);
    }
    return dt;
  }

  public toJSDate(): Date {
    if (DateTime.isDateTime(this.value)) {
      return this.value.toJSDate();
    }
    if (isDate(this.value)) {
      return this.value;
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value).toJSDate();
    }
    if (this.value instanceof NovaDate) {
      return this.value.toJSDate();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public toISOString(): string {
    if (DateTime.isDateTime(this.value)) {
      return this.value.toISO();
    }
    if (isDate(this.value)) {
      return this.value.toISOString();
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value).toISO();
    }
    if (this.value instanceof NovaDate) {
      return this.value.toISOString();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public toISODateString(): string {
    return this.truncate('day').format('yyyy-MM-dd');
  }

  public toDateTime(): DateTime {
    if (DateTime.isDateTime(this.value)) {
      return this.value;
    }
    if (isDate(this.value)) {
      return DateTime.fromJSDate(this.value);
    }
    if (isString(this.value)) {
      return this.stringToDateTime(this.value);
    }
    if (this.value instanceof NovaDate) {
      return this.value.toDateTime();
    }
    throw new NovaDateInvalidValueError(this.value);
  }

  public truncate(unit: DateTimeUnit): NovaDate {
    return new NovaDate(this.toDateTime().startOf(unit));
  }

  /**
   * Formats the DateLike value to `fmt`.
   * If a timezone is specified, it will set the timezone before formatting.
   * If no timezone is specified, but the DateLike value has a timezone
   * defined, it will convert to that timezone before formatting.
   * If no timezone is specified and the DateLike has no timezone or is using
   * the `SystemZone` tz, then it will convert to `UTC` before converting.
   */
  public format(fmt: string, opts: { tz?: string | Zone } = {}): string {
    const dt = this.toDateTime();
    const defaultTz = dt.zone instanceof SystemZone ? 'utc' : dt.zone;
    return dt.setZone(opts?.tz ?? defaultTz).toFormat(fmt);
  }

  /**
   * Set the timezone to `tz` while keeping the global time.
   *
   * @example
   * const [year, month, day, hour, minute] = [2025, 7, 17, 11, 29];
   * const dt = DateTime.fromObject({ year, month, day, hour, minute }, { zone: 'utc' });
   * const brt = FixedOffsetZone.parseSpecifier('UTC-3');
   * new NovaDate(dt).setTz(brt).toISOString() // 2025-07-17T08:29:00.000-03:00
   */
  public setTz(tz: string | Zone): NovaDate {
    return new NovaDate(this.toDateTime().setZone(tz));
  }

  /**
   * Replaces the timezone to `tz` while keeping the local time.
   *
   * @example
   * const [year, month, day, hour, minute] = [2025, 7, 17, 11, 29];
   * const dt = DateTime.fromObject({ year, month, day, hour, minute }, { zone: 'utc' });
   * const brt = FixedOffsetZone.parseSpecifier('UTC-3');
   * new NovaDate(dt).setTz(brt).toISOString() // 2025-07-17T11:29:00.000-03:00
   */
  public replaceTz(tz: string | Zone): NovaDate {
    return new NovaDate(this.toDateTime().setZone(tz, { keepLocalTime: true }));
  }

  public valueOf(): number {
    return this.toDateTime().valueOf();
  }

  /**
   * Compare if two dates represent the same moment regardless of timezone.
   *
   * @param other The NovaDate instance to compare with.
   * @returns True if both instances represent the same date and time, false otherwise.
   *
   * @example ```typescript
   * const dtA = new NovaDate('2025-10-09T14:07:00.000+00:00');
   * const dtB = new NovaDate('2025-10-09T14:07:00.000Z');
   * const dtC = new NovaDate('2025-10-09T14:07:00.000+01:00');
   * console.log(dtA.equals(dtB)); // true
   * console.log(dtA.equals(dtC)); // false
   * ```
   */
  public equals(other: Maybe<NovaDate>): boolean {
    return Number(this) === Number(other);
  }
}
